import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Spin,
  message,
  Typography,
  Alert,
  Descriptions,
  Divider
} from 'antd';
import {
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/operator/governanceService';
import { useInvoiceSettings } from '@config/governance/invoiceSettings';
import ListPageHeader from '@shared/ListPageHeader';

const { Text } = Typography;
const { Option } = Select;

const BillingProfile = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingProfile, setBillingProfile] = useState(null);
  const [countryCode, setCountryCode] = useState(null);

  // Watch carrier_type for dynamic field rendering
  const carrierType = Form.useWatch('carrier_type', form) || 'none';

  // Use invoice settings hook with current country and carrier type
  const invoiceSettings = useInvoiceSettings(countryCode, carrierType);

  // Get current locale for dynamic labels
  const locale = i18n.language?.startsWith('zh') ? 'zh' : 'en';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await governanceService.getBillingProfile();

      if (response.success) {
        setBillingProfile(response.data.billing_profile);
        setCountryCode(response.data.billing_profile?.country_code);

        // Set form values
        form.setFieldsValue(response.data.billing_profile);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch billing profile:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [form, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const response = await governanceService.updateBillingProfile(values);

      if (response.success) {
        message.success(t('operator.governance.billing_profile.save_success'));
        setBillingProfile(response.data.billing_profile);
        form.setFieldsValue(response.data.billing_profile);
      } else {
        message.error(response.message || t('common.error.save_failed'));
      }
    } catch (error) {
      console.error('Failed to update billing profile:', error);
      message.error(t('common.error.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  // Get field configurations with dynamic labels
  const taxIdField = invoiceSettings.getFieldConfig('tax_id', locale);
  const carrierIdField = invoiceSettings.getFieldConfig('carrier_id', locale);
  const loveCodeField = invoiceSettings.getFieldConfig('love_code', locale);

  // Determine which fields to show based on field visibility
  const { fieldVisibility } = invoiceSettings;
  const showTaxIdField = fieldVisibility.tax_id && carrierType === 'company';
  const showCarrierIdField = fieldVisibility.carrier_id && ['mobile_barcode', 'citizen_certificate'].includes(carrierType);
  const showLoveCodeField = fieldVisibility.love_code && carrierType === 'donate';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader items={['governance', 'governance.billing_profile']} />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={billingProfile}
      >
        {/* 基本資訊 */}
        <Card
          title={t('operator.governance.billing_profile.basic_info')}
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            name="billing_name"
            label={t('operator.governance.billing_profile.billing_name')}
            rules={[{ required: true, message: t('operator.governance.billing_profile.billing_name_required') }]}
          >
            <Input placeholder={t('operator.governance.billing_profile.billing_name_placeholder')} />
          </Form.Item>

          <Form.Item
            name="billing_email"
            label={t('operator.governance.billing_profile.billing_email')}
            rules={[
              { required: true, message: t('operator.governance.billing_profile.billing_email_required') },
              { type: 'email', message: t('operator.governance.billing_profile.billing_email_invalid') }
            ]}
          >
            <Input placeholder={t('operator.governance.billing_profile.billing_email_placeholder')} />
          </Form.Item>
        </Card>

        {/* 電子發票設定 (根據國家顯示) */}
        {invoiceSettings.hasInvoiceSettings && invoiceSettings.carrierTypeOptions.length > 0 && (
          <Card
            title={t('operator.governance.billing_profile.invoice_settings')}
            style={{ marginBottom: 16 }}
          >
            <Alert
              message={t('operator.governance.billing_profile.invoice_notice')}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="carrier_type"
              label={t('operator.governance.billing_profile.carrier_type')}
            >
              <Select
                placeholder={t('operator.governance.billing_profile.carrier_type_placeholder')}
                optionLabelProp="label"
              >
                {invoiceSettings.carrierTypeOptions.map(option => (
                  <Option
                    key={option.value}
                    value={option.value}
                    label={locale === 'zh' ? option.label : option.labelEn}
                  >
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontWeight: 500 }}>
                        {locale === 'zh' ? option.label : option.labelEn}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {locale === 'zh' ? option.description : option.descriptionEn}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* 公司發票 - 統一編號 */}
            {showTaxIdField && taxIdField && (
              <Form.Item
                name="tax_id"
                label={locale === 'zh' ? taxIdField.label : taxIdField.labelEn}
                rules={[
                  {
                    required: invoiceSettings.isFieldRequired('tax_id'),
                    message: t('operator.governance.billing_profile.tax_id_required')
                  },
                  {
                    pattern: /^\d{8}$/,
                    message: t('operator.governance.billing_profile.tax_id_invalid')
                  },
                  {
                    validator: async (_, value) => {
                      // Skip if empty or not 8 digits (pattern rule handles format)
                      if (!value || !/^\d{8}$/.test(value)) return;
                      // Only validate checksum for valid format
                      const result = invoiceSettings.validateTaiwanTaxId?.(value);
                      if (result && !result.valid) {
                        throw new Error(result.error);
                      }
                    }
                  }
                ]}
                extra={locale === 'zh' ? taxIdField.hint : taxIdField.hintEn}
              >
                <Input
                  placeholder={locale === 'zh' ? taxIdField.placeholder : taxIdField.placeholderEn}
                  maxLength={taxIdField.maxLength}
                />
              </Form.Item>
            )}

            {/* 手機條碼 / 自然人憑證 */}
            {showCarrierIdField && carrierIdField && (
              <Form.Item
                name="carrier_id"
                label={carrierIdField.label}
                rules={[
                  {
                    required: invoiceSettings.isFieldRequired('carrier_id'),
                    message: t('operator.governance.billing_profile.carrier_id_required')
                  },
                  carrierType === 'mobile_barcode'
                    ? {
                        pattern: /^\/[0-9A-Z.+-]{7}$/,
                        message: t('operator.governance.billing_profile.mobile_barcode_invalid')
                      }
                    : {}
                ]}
                extra={carrierIdField.hint}
              >
                <Input placeholder={carrierIdField.placeholder} />
              </Form.Item>
            )}

            {/* 捐贈碼 */}
            {showLoveCodeField && loveCodeField && (
              <Form.Item
                name="love_code"
                label={locale === 'zh' ? loveCodeField.label : loveCodeField.labelEn}
                rules={[
                  {
                    required: invoiceSettings.isFieldRequired('love_code'),
                    message: t('operator.governance.billing_profile.love_code_required')
                  },
                  {
                    pattern: /^\d{3,7}$/,
                    message: t('operator.governance.billing_profile.love_code_invalid')
                  }
                ]}
                extra={locale === 'zh' ? loveCodeField.hint : loveCodeField.hintEn}
              >
                <Input
                  placeholder={locale === 'zh' ? loveCodeField.placeholder : loveCodeField.placeholderEn}
                  maxLength={loveCodeField.maxLength}
                />
              </Form.Item>
            )}

            {/* 目前發票設定顯示 */}
            {billingProfile?.e_invoice_carrier_display && (
              <>
                <Divider />
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t('operator.governance.billing_profile.current_setting')}>
                    <Text strong>{billingProfile.e_invoice_carrier_display}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </Card>
        )}

        {/* 儲存按鈕 */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
            >
              {t('common.button.save')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default BillingProfile;
